import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { getCasinoGameDetails, getMatchExposure, getMyBets } from "../../../apiservices/CasionApi";
import "../../../styles/games/card32.css";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableCard32 from "../../casinoComponents/BetTableCard32";
import VideoBox from "../../casinoComponents/videoBox";
import Card32CardsOverlay from "../../casinoComponents/videoBox/overlays/Card32CardsOverlay";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import MyBet from "../../casinoComponents/myBet";
import ViewMoreModal from "../../../components/ViewMoreModal";
import ResultCard32 from "../resultui/resultCard32/ResultCard32";
import { extractCasinoGame } from "../../../utils/casinoGame";

// Reference header text before last-results answers (res1.cname replaces it, as on the reference)
const TITLES = { card32: "32 Cards A", pcard32: "Premium 32 Cards", tcard32: "Tembo 32 Cards" };

/**
 * 32 Cards A — routes casino/card32, casino/pcard32, casino/tcard32 (reference casino/card32a, pcard32, tcard32).
 * All three share the reference component (.casino-table.cards32a); each reads its own feed.
 */
export default function Card32() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "card32";
  // res1.cname per feed (the reference header shows it once last results load)
  const [cnames, setCnames] = useState({});
  const title = cnames[gameName] || TITLES[gameName] || "32 Cards A";
  const handleTitle = useCallback((cname) => setCnames((prev) => (prev[gameName] === cname ? prev : { ...prev, [gameName]: cname })), [gameName]);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchGameData();
    const gameDataInterval = setInterval(fetchGameData, 1500);
    return () => clearInterval(gameDataInterval);
  }, [gameName]);

  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const casinoData = gameData?.data?.data || gameData?.data || gameData || {};
  const matchId = (gameData?.data?.data?.mid ?? gameData?.data?.mid ?? gameData?.mid)?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            exposureMap[item.team_name] = parseFloat(item.exposure_amount) || 0;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null
          }));
          setMyBets(formattedBets);
        }
      }
    } catch (error) {
      console.error('Error fetching my bets:', error);
    }
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;

    const timeoutId = setTimeout(() => {
      fetchExposure();
      fetchMyBetsData();
    }, 2000);

    const intervalId = setInterval(() => {
      fetchExposure();
      fetchMyBetsData();
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchExposure, fetchMyBetsData, matchId]);

  // Admin panel places no bets: an odds click only refreshes exposure and My Bets
  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  // Reference: mainData = sub filtered by subtype "card32", ordered by sr
  const mainData = [...(casinoData?.sub || [])]
    .filter((s) => !s?.subtype || s.subtype === "card32")
    .sort((a, b) => (a?.sr ?? 0) - (b?.sr ?? 0));
  const remark = casinoData?.remark;

  return (
    <CasinoTableLayout
      tableClass="cards32a"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={casinoData?.mid ?? ""} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={casinoData?.lt}
          gameType={gameName}
          cardsOverlay={<Card32CardsOverlay cardString={casinoData?.card} variant="a" />}
        />{" "}
        {matchId ? (
          <div className="casino-detail">
            <BetTableCard32 data={mainData} onBetClick={handleBetClick} exposures={exposures} />{" "}
            {remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} />
                </div>{" "}
                <marquee>{remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              resultComponent={<ResultCard32 gameType={gameName} data={gameData?.lrs || []} onTitle={handleTitle} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
